import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { History, Trash2, Clock, SquarePen } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Skeleton } from '@/components/ui/skeleton';
import { motion, AnimatePresence } from 'framer-motion';

const HISTORY_LIMIT = 10;

const CommandHistoryButton = ({ onLoadHistory, setIsLoading: setMainLoading, onNewConversation }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [histories, setHistories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const { axiosInstance } = useAuth();

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8, // 8px gap below button
        right: window.innerWidth - rect.right
      });
    }
  }, [isOpen]);

  // Fetch command histories when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchHistories();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const fetchHistories = async (loadMore = false) => {
    if (loadMore) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    
    try {
      const skip = loadMore ? histories.length : 0;
      const response = await axiosInstance.get(`/commands?limit=${HISTORY_LIMIT}&skip=${skip}`);
      const newHistories = response.data.commandHistories || [];
      
      if (loadMore) {
        setHistories(prev => [...prev, ...newHistories]);
      } else {
        setHistories(newHistories);
      }
      setHasMore(response.data.hasMore || false);
    } catch (error) {
      console.error('Failed to fetch command histories:', error);
    } finally {
      if (loadMore) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  const handleLoadHistory = async (historyId) => {
    setIsOpen(false);  // Close dropdown immediately
    setMainLoading(true);  // Show main loading spinner
    
    try {
      const response = await axiosInstance.get(`/commands/${historyId}`);
      onLoadHistory(response.data.commandHistory);
    } catch (error) {
      console.error('Failed to load command history:', error);
      setMainLoading(false);  // Clear loading on error
    }
    // Note: loading cleared by handleLoadHistory in context after state is restored
  };

  const handleClearHistory = async (historyId, event) => {
    event.stopPropagation();
    try {
      await axiosInstance.delete(`/commands/${historyId}`);
      // Remove from local state
      setHistories(histories.filter(h => h._id !== historyId));
    } catch (error) {
      console.error('Failed to delete command history:', error);
    }
  };

  const handleNewConversation = () => {
    setIsOpen(false);
    onNewConversation();
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="-mr-1.5 flex items-center justify-center h-7 w-7 rounded-lg hover:bg-violet-500/15 text-violet-500 transition-colors"
        title="Command History"
      >
        <History className="h-4 w-4" />
      </button>

      {isOpen && createPortal(
        <AnimatePresence>
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -8 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="fixed w-80 bg-white/70 dark:bg-black/70 rounded-xl border border-violet-500/20 dark:border-violet-500/30 shadow-2xl shadow-violet-500/10 overflow-hidden z-50 backdrop-blur-sm"
            style={{
              top: `${dropdownPosition.top}px`,
              right: `${dropdownPosition.right}px`
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-violet-50/80 to-violet-50/40 dark:from-violet-950/40 dark:to-violet-950/20 border-b border-violet-500/10 dark:border-violet-500/20">
              <span className="text-sm font-semibold text-violet-900 dark:text-violet-100">Command History</span>
              <button
                onClick={handleNewConversation}
                className="flex items-center justify-center h-7 w-7 rounded-md hover:bg-violet-100 dark:hover:bg-violet-900/30 text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 transition-colors"
                title="New conversation"
              >
                <SquarePen className="h-4 w-4" />
              </button>
            </div>

            {/* History List */}
            <div className="max-h-[400px] overflow-y-auto">
              {isLoading ? (
                <div className="p-2">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex items-center gap-3 px-3 py-2.5 mx-1 my-2 rounded-lg">
                      <div className="flex-1 space-y-2">
                        <div className="h-[14px] bg-black/10 dark:bg-white/10 rounded animate-pulse w-4/5"></div>
                        <div className="h-3 bg-black/10 dark:bg-white/10 rounded animate-pulse w-1/4"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : histories.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="rounded-full bg-violet-100 dark:bg-violet-900/20 p-3 mb-3">
                    <History className="size-5 text-violet-600 dark:text-violet-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                    No history yet
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Your command history will appear here
                  </p>
                </div>
              ) : (
                <div className="p-2">
                  {histories.map((history) => (
                    <div
                      key={history._id}
                      onClick={() => handleLoadHistory(history._id)}
                      className="group flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-violet-50 dark:hover:bg-violet-950/30 transition-all duration-200 border border-transparent hover:border-violet-200 dark:hover:border-violet-800/50"
                    >
                      <div className="flex-1 min-w-0 space-y-1">
                        <p className="text-sm leading-tight truncate font-medium text-gray-900 dark:text-gray-100">
                          {history.title}
                        </p>
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                          <Clock className="h-3 w-3" />
                          <span>{formatTimeAgo(history.updatedAt)}</span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => handleClearHistory(history._id, e)}
                        className="opacity-0 group-hover:opacity-100 flex items-center justify-center h-7 w-7 rounded-md hover:bg-red-50 dark:hover:bg-red-950/30 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-all flex-shrink-0"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  
                  {/* Load More Button */}
                  {hasMore && (
                    <button
                      onClick={() => fetchHistories(true)}
                      disabled={isLoadingMore}
                      className="w-full mt-2 py-2 text-sm text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-950/30 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {isLoadingMore ? 'Loading...' : 'Load more'}
                    </button>
                  )}
                </div>
              )}
          </div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default CommandHistoryButton;

